// Generischer Overpass-POI-Layer (Multi-Kategorie)
// =================================================
//
// Rendert ALLE Overpass-Kategorien außer drinking_water (das hat in MapPage
// seine eigene gemischte OSM+admin+user-Pipeline). Eine Source, ein Symbol-
// Layer, data-driven `icon-image: ['get','iconKey']` — pro Kategorie ein
// Emoji-Icon (Kreis in Kategorie-Farbe + weißer Rand + Emoji). Sichtbarkeit/
// Filter passiert über die Feature-Liste, die MapPage per setData reinkippt
// (nur aktivierte Kategorien).
//
// Bewusst parallel zu poiSymbol.ts (drinking_water) statt es umzubauen — additiv,
// damit die bestehende, fein austarierte drinking_water-Logik unberührt bleibt.

import type maplibregl from 'maplibre-gl';
import { POI_CATEGORIES, OVERPASS_EXTRA_CATEGORIES, type PoiCategoryId } from '../poi';
import { POI_MIN_ZOOM } from './poiSymbol';

export const OVERPASS_POI_SOURCE = 'adt-overpass-poi';
export const OVERPASS_POI_LAYER = 'adt-overpass-poi-symbols';
// Ring um den aktuell ausgewählten POI, damit man weiß welcher angetippt ist.
export const OVERPASS_POI_HIGHLIGHT_LAYER = 'adt-overpass-poi-highlight';

export function overpassIconKey(category: PoiCategoryId): string {
  return `adt-overpass-icon-${category}`;
}

/** Zeichnet einen Emoji-Pin (farbiger Kreis + weißer Rand + Emoji) als Map-Icon. */
function drawIcon(map: maplibregl.Map, category: PoiCategoryId): void {
  const key = overpassIconKey(category);
  if (map.hasImage(key)) return;
  const def = POI_CATEGORIES[category];
  const SIZE = 52;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const radius = SIZE / 2 - 4;
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = def.color ?? 'rgba(80,80,80,0.95)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '26px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  ctx.fillText(def.emoji, cx, cy + 1);

  const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
  map.addImage(key, imageData as unknown as HTMLImageElement, { pixelRatio: 2 });
}

/** Alle Kategorie-Icons registrieren (idempotent). */
export function ensureOverpassPoiIcons(map: maplibregl.Map): void {
  for (const cat of OVERPASS_EXTRA_CATEGORIES) drawIcon(map, cat);
}

/** Source + Symbol-Layer anlegen (idempotent). */
export function ensureOverpassPoiSourceAndLayer(map: maplibregl.Map): void {
  if (!map.getSource(OVERPASS_POI_SOURCE)) {
    map.addSource(OVERPASS_POI_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  // Highlight-Ring ZUERST anlegen (unter den Symbolen), Filter matcht initial
  // nichts. setOverpassPoiHighlight() schaltet ihn auf die ausgewählte id.
  if (!map.getLayer(OVERPASS_POI_HIGHLIGHT_LAYER)) {
    map.addLayer({
      id: OVERPASS_POI_HIGHLIGHT_LAYER,
      type: 'circle',
      source: OVERPASS_POI_SOURCE,
      minzoom: POI_MIN_ZOOM,
      filter: ['==', ['get', 'id'], '__none__'],
      paint: {
        'circle-radius': 22,
        'circle-color': 'rgba(56,189,248,0.18)',
        'circle-stroke-width': 4,
        'circle-stroke-color': '#38bdf8',
        'circle-stroke-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer(OVERPASS_POI_LAYER)) {
    map.addLayer({
      id: OVERPASS_POI_LAYER,
      type: 'symbol',
      source: OVERPASS_POI_SOURCE,
      minzoom: POI_MIN_ZOOM,
      layout: {
        'icon-image': ['get', 'iconKey'],
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
}

/** Setzt/entfernt den Highlight-Ring auf den POI mit der gegebenen id. */
export function setOverpassPoiHighlight(
  map: maplibregl.Map,
  id: string | null,
): void {
  if (!map.getLayer(OVERPASS_POI_HIGHLIGHT_LAYER)) return;
  map.setFilter(OVERPASS_POI_HIGHLIGHT_LAYER, ['==', ['get', 'id'], id ?? '__none__']);
}

export function setOverpassPoiData(
  map: maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.Point>[],
): void {
  const src = map.getSource(OVERPASS_POI_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  src.setData({ type: 'FeatureCollection', features });
}

export function removeOverpassPoiLayer(map: maplibregl.Map): void {
  try {
    if (map.getLayer(OVERPASS_POI_LAYER)) map.removeLayer(OVERPASS_POI_LAYER);
    if (map.getLayer(OVERPASS_POI_HIGHLIGHT_LAYER))
      map.removeLayer(OVERPASS_POI_HIGHLIGHT_LAYER);
    if (map.getSource(OVERPASS_POI_SOURCE)) map.removeSource(OVERPASS_POI_SOURCE);
  } catch {
    // Map bereits zerstört — nichts zu tun.
  }
}
